import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  FAMILY_SITE_EMOJI_SUGGESTIONS,
  FAMILY_SITE_LUCIDE_ICONS,
  FAMILY_SITE_LUCIDE_NAMES,
  getFamilySiteLucideIcon,
} from '@/lib/family-site-icons';
import {
  FAMILY_SITE_LOGO_ALLOWED_MIMES,
  FAMILY_SITE_LOGO_MAX_BYTES,
  type FamilySiteLogoType,
} from '@innermate/shared';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

interface FamilySiteLogoPickerProps {
  logoType: FamilySiteLogoType;
  logoEmoji: string | null;
  logoLucideName: string | null;
  /** image 타입 + 이미 업로드된 이미지가 있을 때 미리보기용 URL (`/api/family-sites/:id/logo?v=ts`). */
  imageUrl: string | null;
  onChangeType: (type: FamilySiteLogoType) => void;
  onChangeEmoji: (value: string) => void;
  onChangeLucide: (name: string) => void;
  /** 사용자가 파일 선택 시 호출. 호출자는 mutation 으로 업로드. id 가 없는 신규 상태일 수도 있으므로 호출자가 처리. */
  onPickFile?: (file: File) => void;
  imagePending?: boolean;
  imageError?: string | null;
  /** 신규 폼에서는 image 탭에서 안내 문구만 보여주고 업로드는 저장 후로 미루기 위함. */
  imageDisabledReason?: string;
}

type TabKey = FamilySiteLogoType;

const TAB_LABELS: Record<TabKey, string> = {
  emoji: '이모지',
  lucide: 'Lucide 아이콘',
  image: '이미지 업로드',
};

export function FamilySiteLogoPicker({
  logoType,
  logoEmoji,
  logoLucideName,
  imageUrl,
  onChangeType,
  onChangeEmoji,
  onChangeLucide,
  onPickFile,
  imagePending,
  imageError,
  imageDisabledReason,
}: FamilySiteLogoPickerProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 border-b border-border">
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onChangeType(key)}
            className={cn(
              'px-3 py-2 text-sm border-b-2 -mb-px transition-colors',
              logoType === key
                ? 'border-accent text-foreground font-medium'
                : 'border-transparent text-foreground-muted hover:text-foreground',
            )}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>

      {logoType === 'emoji' ? (
        <EmojiTab value={logoEmoji} onChange={onChangeEmoji} />
      ) : logoType === 'lucide' ? (
        <LucideTab value={logoLucideName} onChange={onChangeLucide} />
      ) : (
        <ImageTab
          imageUrl={imageUrl}
          onPickFile={onPickFile}
          pending={imagePending}
          error={imageError}
          disabledReason={imageDisabledReason}
        />
      )}
    </div>
  );
}

function EmojiTab({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="size-12 rounded-md border border-border bg-surface-muted grid place-items-center text-2xl">
          {value || '🏢'}
        </div>
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value.slice(0, 8))}
          placeholder="이모지 입력 (예: 🏢)"
          className="flex-1"
        />
      </div>
      <div>
        <p className="text-xs text-foreground-muted mb-2">자주 쓰는 이모지</p>
        <div className="grid grid-cols-10 gap-1.5">
          {FAMILY_SITE_EMOJI_SUGGESTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onChange(e)}
              className={cn(
                'size-9 rounded-md grid place-items-center text-lg border transition-colors',
                value === e ? 'border-accent bg-accent/10' : 'border-border hover:bg-surface-muted',
              )}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LucideTab({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (name: string) => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAMILY_SITE_LUCIDE_NAMES;
    return FAMILY_SITE_LUCIDE_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  const SelectedIcon = getFamilySiteLucideIcon(value);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="size-12 rounded-md border border-border bg-surface-muted grid place-items-center">
          {SelectedIcon ? (
            <SelectedIcon className="size-6 text-foreground" />
          ) : (
            <ImageIcon className="size-5 text-foreground-muted" />
          )}
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="아이콘 검색 (예: building, globe)"
          className="flex-1"
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-surface-muted/30 p-2">
        <div className="grid grid-cols-8 gap-1.5">
          {filtered.map((name) => {
            const Icon = FAMILY_SITE_LUCIDE_ICONS[name];
            if (!Icon) return null;
            const selected = name === value;
            return (
              <button
                key={name}
                type="button"
                onClick={() => onChange(name)}
                title={name}
                className={cn(
                  'size-10 rounded-md grid place-items-center border transition-colors',
                  selected
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-transparent text-foreground-muted hover:bg-surface hover:text-foreground',
                )}
              >
                <Icon className="size-4" />
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <p className="col-span-8 text-center text-xs text-foreground-muted py-6">
              검색 결과가 없습니다.
            </p>
          ) : null}
        </div>
      </div>
      {value ? (
        <p className="text-xs text-foreground-muted">
          선택: <code className="font-mono">{value}</code>
        </p>
      ) : null}
    </div>
  );
}

function ImageTab({
  imageUrl,
  onPickFile,
  pending,
  error,
  disabledReason,
}: {
  imageUrl: string | null;
  onPickFile?: (file: File) => void;
  pending?: boolean;
  error?: string | null;
  disabledReason?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const onPick = () => {
    setLocalError(null);
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(FAMILY_SITE_LOGO_ALLOWED_MIMES as readonly string[]).includes(file.type)) {
      setLocalError('PNG / JPEG / WebP / SVG 파일만 업로드할 수 있습니다.');
      e.target.value = '';
      return;
    }
    if (file.size > FAMILY_SITE_LOGO_MAX_BYTES) {
      setLocalError('파일 크기가 2MB 를 초과합니다.');
      e.target.value = '';
      return;
    }
    onPickFile?.(file);
    e.target.value = '';
  };

  if (disabledReason) {
    return (
      <div className="rounded-md border border-dashed border-border bg-surface-muted/30 p-4 text-sm text-foreground-muted">
        {disabledReason}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <div className="size-16 rounded-md border border-border bg-surface-muted grid place-items-center overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt="로고" className="size-full object-contain" />
          ) : (
            <ImageIcon className="size-6 text-foreground-muted" />
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="text-xs text-foreground-muted">
            1:1 비율 권장 (예: 256×256). PNG / JPEG / WebP / SVG, 최대 2MB.
          </p>
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={FAMILY_SITE_LOGO_ALLOWED_MIMES.join(',')}
              onChange={onChange}
              className="hidden"
            />
            <Button type="button" onClick={onPick} disabled={pending}>
              <Upload className="size-4" />
              {pending ? '업로드 중…' : imageUrl ? '이미지 변경' : '이미지 업로드'}
            </Button>
          </div>
        </div>
      </div>
      {localError || error ? (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <X className="size-3.5" />
          {localError ?? error}
        </p>
      ) : null}
    </div>
  );
}
